import { useState } from 'preact/hooks';
import { Spinner } from '../../components/ui/Spinner.js';
import { api } from '../../services/api.js';
// 065: the sentences live in one place shared with the voucher tender beside this one (FR-010).
import { giftCertReasonMessage } from './tenderWording.js';

interface ValidateResponse {
  valid: boolean;
  code?: string;
  balance_minor?: number;
  status?: string;
  reason?: string;
}

/** Preview lines from the live quote, threaded down so this step can show credit + amount due. */
export interface GiftCertQuoteInfo {
  applied: boolean;
  reason?: string;
  credit_minor?: number;
  balance_after_minor?: number;
  amount_due_minor?: number;
}

interface GiftCertStepProps {
  /** Controlled gift-cert code (uppercased). */
  code: string;
  onChange: (code: string) => void;
  /** When true a voucher is applied → gift cert is mutually exclusive and this step is disabled. */
  isVoucherApplied?: boolean;
  /** 358: "Use my credit" is on; a code is a second tender, so this step steps aside. */
  isCreditApplied?: boolean;
  /**
   * 075: true while a voucher code is in the box and the platform has not answered about **that**
   * code yet. The step stays on screen with its input disabled — see the render branch below for why
   * that is not the same as either of the other two states (FR-007).
   */
  isVerdictPending?: boolean;
  /** Optional live quote gift-cert preview (credit + amount due). */
  quote?: GiftCertQuoteInfo;
}

function rm(minor: number): string {
  return `RM ${(minor / 100).toFixed(2)}`;
}

export function GiftCertStep({
  code,
  onChange,
  isVoucherApplied,
  isCreditApplied,
  isVerdictPending,
  quote,
}: GiftCertStepProps) {
  const [result, setResult] = useState<ValidateResponse | null>(null);
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState('');

  async function check() {
    const trimmed = code.trim();
    if (!trimmed) return;
    setChecking(true);
    setError('');
    setResult(null);
    try {
      const res = await api.get<ValidateResponse>(
        `/gift-certificates/validate?code=${encodeURIComponent(trimmed)}`,
      );
      setResult(res);
    } catch (err) {
      setError((err as { message?: string }).message ?? 'Failed to check the credit code');
    } finally {
      setChecking(false);
    }
  }

  // 358 (FR-005): customers read "credit"; "gift certificate" stays in the admin.
  if (isVoucherApplied || isCreditApplied) {
    return (
      <div className="flex flex-col gap-1">
        {/* No input in this branch, so this caption is a heading rather than a `label`. */}
        <p className="text-sm text-gray-400">Credit code</p>
        <p className="text-xs text-gray-500">
          {isVoucherApplied
            ? 'A voucher is applied. Remove it to use a credit code instead.'
            : 'Your credit is applied. Remove it to use a credit code instead.'}
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      {/* This box sits directly under the booking form's voucher box. Both used to say
          "ENTER CODE", so the two tenders were indistinguishable; the placeholder now names
          this one and `htmlFor` binds the caption as the field's accessible name. */}
      <label className="text-sm text-gray-400" htmlFor="gift-cert-code">
        Have a credit code? (optional)
      </label>
      <div className="flex gap-2">
        <input
          id="gift-cert-code"
          type="text"
          className="flex-1 bg-surface-mid text-text rounded-lg px-4 py-3 uppercase disabled:opacity-50"
          value={code}
          onInput={(e) => {
            onChange((e.target as HTMLInputElement).value.toUpperCase());
            setResult(null);
            setError('');
          }}
          placeholder="ENTER CREDIT CODE"
          /* 075: held while the voucher's fate is undecided (FR-007). Disabling the control rather
             than removing the step is what makes "no request ever carries both codes" structural —
             a code that cannot be typed cannot be sent. `aria-describedby` points at the reason, so
             a renter who cannot see the voucher box's spinner is still told why this is inert. */
          disabled={isVerdictPending}
          aria-describedby={isVerdictPending ? 'gift-cert-held-reason' : undefined}
        />
        <button
          type="button"
          className="rounded-lg bg-secondary px-4 py-3 text-sm font-semibold text-on-secondary disabled:opacity-50"
          disabled={isVerdictPending || checking || code.trim().length === 0}
          onClick={check}
        >
          {checking ? <Spinner size="sm" /> : 'Check'}
        </button>
      </div>

      {/* The reason the control above is inert. Not a new tender sentence — `tenderWording.ts` owns
          the verdict vocabulary and is deliberately untouched by this feature (FR-013); this states
          a transient UI condition, and it is the accessible description the disabled input points
          at rather than a message about a code. */}
      {isVerdictPending && (
        <p id="gift-cert-held-reason" className="text-xs text-gray-500">
          Checking the voucher code first.
        </p>
      )}

      {error && <p className="text-sm text-red-400">{error}</p>}

      {result?.valid && (
        <div className="bg-surface-card rounded-lg p-3 flex flex-col gap-1">
          <div className="flex justify-between text-sm text-green-400">
            <span>Credit available</span>
            <span>{rm(result.balance_minor ?? 0)}</span>
          </div>
          {quote?.applied && quote.credit_minor !== undefined && (
            <div className="flex justify-between text-sm text-gray-300">
              <span>Credit applied</span>
              <span>-{rm(quote.credit_minor)}</span>
            </div>
          )}
          {quote?.applied && quote.amount_due_minor !== undefined && (
            <div className="flex justify-between text-sm text-text font-semibold">
              <span>Amount due</span>
              <span>{rm(quote.amount_due_minor)}</span>
            </div>
          )}
        </div>
      )}

      {result && !result.valid && (
        <p className="text-sm text-red-400">{giftCertReasonMessage(result.reason)}</p>
      )}
    </div>
  );
}
